import React, { useState } from 'react';
import { Users, AlertTriangle, CheckCircle, Plus, Search } from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
import { useStorageState } from '../../../../hooks/useStorageState';

/**
 * Pilot Operations Tab - Track pilot transfers and related operations
 */
const PilotOperationsTab = ({ currentVessel, onAddEntry }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Storage for pilot transfers
  const storageKey = currentVessel?.id ? `pilot_transfers_${currentVessel.id}` : 'pilot_transfers';
  const { value: transfers, setValue: saveTransfers } = useStorageState(storageKey, []);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    pilotName: '',
    vesselBoarded: '', // Name of vessel being boarded
    vesselIMO: '', // IMO number of vessel (optional)
    embarked: true, // true = embarked, false = disembarked
    position: { latitude: '', longitude: '' },
    ladderSide: 'port',
    ladderHeight: '',
    weatherConditions: '',
    seaState: '',
    manRopes: false,
    pilotHoist: false,
    combinationLadder: false,
    notes: ''
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newTransfer = {
      id: Date.now().toString(),
      ...formData,
      timestamp: new Date(`${formData.date}T${formData.time}`).toISOString(),
      vesselName: currentVessel?.name
    };
    
    const updatedTransfers = [newTransfer, ...transfers].sort((a, b) => 
      new Date(b.timestamp) - new Date(a.timestamp)
    );
    
    await saveTransfers(updatedTransfers);
    
    // Also add to logbook
    onAddEntry({
      type: 'PILOT_TRANSFER',
      category: 'operations',
      summary: `Pilot ${formData.embarked ? 'embarked' : 'disembarked'}: ${formData.pilotName}${formData.vesselBoarded ? ` - ${formData.vesselBoarded}` : ''}`,
      description: `Ladder on ${formData.ladderSide} side, ${formData.ladderHeight}m`,
      details: newTransfer
    });
    
    setShowAddForm(false);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      pilotName: '',
      vesselBoarded: '',
      vesselIMO: '',
      embarked: true,
      position: { latitude: '', longitude: '' },
      ladderSide: 'port',
      ladderHeight: '',
      weatherConditions: '',
      seaState: '',
      manRopes: false,
      pilotHoist: false,
      combinationLadder: false,
      notes: ''
    });
  };
  
  const filteredTransfers = transfers.filter(t => 
    searchQuery === '' || 
    JSON.stringify(t).toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const stats = {
    total: transfers.length,
    thisMonth: transfers.filter(t => {
      const date = new Date(t.timestamp);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length,
    lastTransfer: transfers[0]?.timestamp ? new Date(transfers[0].timestamp).toLocaleDateString() : 'N/A'
  };
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Users className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Pilot Transfer Records</h2>
                <p className="text-sm text-gray-600">Track pilot embarkation and disembarkation</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Transfer
            </button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="px-6 py-4 grid grid-cols-3 gap-6">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Transfers</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">This Month</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.thisMonth}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Last Transfer</p>
            <p className="text-sm font-medium text-gray-900 mt-1">{stats.lastTransfer}</p>
          </div>
        </div>
      </div>
      
      {/* Requirements Notice */}
      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Pilot Transfer Requirements (IMO A.1045):</p>
            <ul className="space-y-0.5 text-xs">
              <li>• Record all pilot transfers with time and position</li>
              <li>• Document ladder arrangement and safety measures</li>
              <li>• Note weather conditions and sea state</li>
              <li>• Maintain records for port state inspections</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-sm font-medium text-gray-900">Record Pilot Transfer</h3>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Time</label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Operation</label>
                <select
                  value={formData.embarked}
                  onChange={(e) => setFormData({ ...formData, embarked: e.target.value === 'true' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="true">Pilot Embarked</option>
                  <option value="false">Pilot Disembarked</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Pilot Name</label>
              <input
                type="text"
                value={formData.pilotName}
                onChange={(e) => setFormData({ ...formData, pilotName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Vessel Name</label>
                <input
                  type="text"
                  value={formData.vesselBoarded}
                  onChange={(e) => setFormData({ ...formData, vesselBoarded: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Name of vessel boarded"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Vessel IMO (Optional)</label>
                <input
                  type="text"
                  value={formData.vesselIMO}
                  onChange={(e) => setFormData({ ...formData, vesselIMO: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="IMO number"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Ladder Side</label>
                <select
                  value={formData.ladderSide}
                  onChange={(e) => setFormData({ ...formData, ladderSide: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="port">Port</option>
                  <option value="starboard">Starboard</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Ladder Height (m)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.ladderHeight}
                  onChange={(e) => setFormData({ ...formData, ladderHeight: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-700">Equipment Used</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.manRopes}
                    onChange={(e) => setFormData({ ...formData, manRopes: e.target.checked })}
                  />
                  <span className="text-sm">Man Ropes</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.pilotHoist}
                    onChange={(e) => setFormData({ ...formData, pilotHoist: e.target.checked })}
                  />
                  <span className="text-sm">Pilot Hoist</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.combinationLadder}
                    onChange={(e) => setFormData({ ...formData, combinationLadder: e.target.checked })}
                  />
                  <span className="text-sm">Combination Ladder</span>
                </label>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
              >
                Save Transfer
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Records Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900">Transfer History</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search transfers..."
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pilot</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vessel</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Operation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ladder</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Equipment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTransfers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500">
                    No pilot transfers recorded
                  </td>
                </tr>
              ) : (
                filteredTransfers.map(transfer => (
                  <tr key={transfer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(transfer.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {transfer.pilotName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        {transfer.vesselBoarded || '-'}
                        {transfer.vesselIMO && (
                          <div className="text-xs text-gray-500">IMO: {transfer.vesselIMO}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge color={transfer.embarked ? 'green' : 'yellow'} size="sm">
                        {transfer.embarked ? 'Embarked' : 'Disembarked'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {transfer.ladderSide} / {transfer.ladderHeight}m
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {[
                        transfer.manRopes && 'Man ropes',
                        transfer.pilotHoist && 'Hoist',
                        transfer.combinationLadder && 'Combination'
                      ].filter(Boolean).join(', ') || 'Standard ladder'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PilotOperationsTab;